import { useRef, type ChangeEvent } from "react";
import { useFormContext } from "react-hook-form";

import { Accordion } from "ui/accordion";
import { Box } from "ui/box";
import { Button } from "ui/button";
import { IconButton } from "ui/icon-button";
import { Typography } from "ui/typography";
import toast from "components/partials/toast/toast";

import { useCurrentClient } from "hooks/api/use-clients";
import { useUploadThemeAsset } from "hooks/api/use-theme";

import type { EmailConfigurationFormValues } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.types";

import {
  BANNER_FILE_NAME_SX,
  BANNER_FILE_SX,
  FIELDS_SX,
  SPECIFICATION_DETAIL_SX,
  SPECIFICATION_TERM_SX,
  SPECIFICATIONS_SX,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.styles";
import {
  BANNER_ACCEPT,
  BANNER_MAX_FILE_SIZE,
  BANNER_SPECIFICATIONS,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.constants";

import Icons from "assets";

type BannerSectionProps = { disabled: boolean };

export const BannerSection = ({ disabled }: BannerSectionProps) => {
  const client = useCurrentClient();
  const uploadThemeAsset = useUploadThemeAsset(client.id);
  const { setValue, watch } = useFormContext<EmailConfigurationFormValues>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const imageUrl = watch("banner.imageUrl");
  const fileName = watch("banner.fileName");

  const setBanner = (url: string, name: string) => {
    setValue("banner.imageUrl", url, { shouldDirty: true });
    setValue("banner.fileName", name, { shouldDirty: true });
  };

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (file.size > BANNER_MAX_FILE_SIZE) {
      toast.error({ data: { title: "Failed to upload file", message: "File must be less than 256 KB" } });
      clearFileInput();

      return;
    }

    try {
      const { url } = await uploadThemeAsset.mutateAsync(file);

      if (!url) {
        throw new Error("No response URL received");
      }

      setBanner(url, file.name);
    } catch {
      toast.error({
        data: {
          title: "Failed to upload file",
          message: "There was an error uploading your image. Please try again.",
        },
      });
      clearFileInput();
    }
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleDelete = () => {
    setBanner("", "");
    clearFileInput();
  };

  return (
    <Accordion title="Banner" defaultExpanded>
      <Box sx={FIELDS_SX}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={BANNER_ACCEPT}
          aria-label="Banner image"
          hidden
        />
        {imageUrl ? (
          <Box sx={BANNER_FILE_SX}>
            <Typography variant="body2" component="span" sx={BANNER_FILE_NAME_SX}>
              {fileName || imageUrl}
            </Typography>
            <IconButton aria-label="Delete banner" size="large" variant="text" disabled={disabled} onClick={handleDelete}>
              <Icons.Trash />
            </IconButton>
          </Box>
        ) : (
          <Button
            variant="outlined"
            size="large"
            fullWidth
            disabled={disabled}
            loading={uploadThemeAsset.isPending}
            startIcon={<Icons.LinkIndicator />}
            onClick={handleUploadClick}>
            Upload
          </Button>
        )}
        <Box component="dl" sx={SPECIFICATIONS_SX}>
          {BANNER_SPECIFICATIONS.map(({ label, value }) => (
            <Box key={label}>
              <Box component="dt" sx={SPECIFICATION_TERM_SX}>
                {label}
              </Box>
              <Box component="dd" sx={SPECIFICATION_DETAIL_SX}>
                {value}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Accordion>
  );
};
